// src/components/frontend/pages/HotelDetailPage.js

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import { MaterialIcon } from "../../utils/materialIcons";
import { Button } from "@mui/material";
import { useI18n } from "../../../i18n/I18nContext";
import { useAuth } from "../../utils/AuthContext";

import {
    Box,
    Typography,
    CircularProgress,
    Container,
    Paper,
    Link,
    Dialog,
    IconButton,
    Divider,
    Chip,
    Stack
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";

import HotelsMap from "../companies/Map";
import { cropImageStyle } from "../../backend/companies/utils/companyUtils";
import { ClassificationIcon } from "../../utils/classificationIcons";
import { AwardBadge } from "../../utils/awardIcons";
import { LocationIcon } from "../../utils/locationIcons";
import { LeisureActivityIcon } from "../../utils/leisureActivityIcons";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const resolveImg = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    const base = String(API_BASE || "").replace(/\/$/, "");
    const p = s.startsWith("/") ? s : `/${s}`;
    return `${base}${p}`;
};

function formatClassification(item) {
    if (!item) return "";
    const label = item.label_de || item.name || "";
    const value = item.value;
    const hasValue = value !== undefined && value !== null && String(value).trim() !== "";
    if (item.value_type === "boolean") return label;
    if (!hasValue) return label;
    return `${value} ${label}${item.is_superior ? " Superior" : ""}`.trim();
}

function visibleRoomQuantities(hotel) {
    return (Array.isArray(hotel?.room_quantities) ? hotel.room_quantities : [])
        .filter((item) => {
            const value = Number(item.quantity);
            return Number.isFinite(value) && value > 0;
        });
}

function visibleRoomDetails(hotel) {
    return (Array.isArray(hotel?.room_details) ? hotel.room_details : [])
        .filter((item) => String(item.custom_label || item.label_de || item.name || "").trim());
}

function visibleRoomPrices(hotel) {
    return (Array.isArray(hotel?.room_prices) ? hotel.room_prices : [])
        .filter((item) => {
            return ["custom_label", "reference", "price_min", "price_max"].some((field) => (
                String(item[field] || "").trim()
            )) || !!item.is_from;
        });
}

function visibleRoomPriceSpecials(hotel) {
    return (Array.isArray(hotel?.room_price_specials) ? hotel.room_price_specials : [])
        .filter((item) => String(item.custom_label || item.label_de || item.name || "").trim());
}

function breakfastLabel(value) {
    const map = {
        1: "keine Angaben",
        2: "Preise inkl. Frühstück",
        3: "Preise inkl. Frühstücksbuffet",
        4: "Preise exkl. Frühstück",
    };
    return map[Number(value)] || "";
}

function formatRoomPrice(item, currency) {
    const min = String(item.price_min || "").trim();
    const max = String(item.price_max || "").trim();
    const prefix = item.is_from ? "ab " : "";
    if (min && max) return `${prefix}${min} - ${max} ${currency}`.trim();
    if (min) return `${prefix}${min} ${currency}`.trim();
    if (max) return `${max} ${currency}`.trim();
    return item.is_from ? "Preis ab" : "";
}

function setNamedMeta(name, content) {
    const value = String(content || "").trim();
    const tags = Array.from(document.querySelectorAll(`meta[name="${name}"]`));
    let tag = tags[0];
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tags.slice(1).forEach((duplicateTag) => duplicateTag.remove());
    tag.setAttribute("content", value);
}

function buildFallbackMetaDescription(hotel) {
    return [
        hotel?.name,
        [hotel?.street, hotel?.zip, hotel?.city].filter(Boolean).join(", "),
        hotel?.country,
    ].filter(Boolean).join(" - ");
}

function buildFallbackMetaKeywords(hotel) {
    const values = [
        hotel?.name,
        hotel?.city,
        hotel?.country,
        ...(Array.isArray(hotel?.categories) ? hotel.categories.map((item) => item.label_de || item.name) : []),
        ...(Array.isArray(hotel?.cuisines) ? hotel.cuisines.map((item) => item.label_de || item.name) : []),
    ];
    return [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))].join(", ");
}

export default function HotelDetailPage() {

    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchString = searchParams.toString();
    const { t } = useI18n();
    const { user } = useAuth();
    const printMode = searchParams.get("print") === "1";
    const screenshotMode = searchParams.get("screenshot") === "1";
    const captureMode = printMode || screenshotMode;

    const [hotel, setHotel] = useState(null);
    const [loading, setLoading] = useState(true);
    const trackedViewRef = React.useRef(null);

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [activeImage, setActiveImage] = useState(0);
    const [menuLightboxOpen, setMenuLightboxOpen] = useState(false);
    const [activeMenuPage, setActiveMenuPage] = useState(0);

    useEffect(() => {

        const loadHotel = async () => {

            try {

                const isNumericId = /^\d+$/.test(String(id || ""));
                const detailEndpoint = isNumericId
                    ? `${API_BASE}/api/companies/${id}`
                    : `${API_BASE}/api/companies/by-slug/${encodeURIComponent(id)}`;

                const res = await axios.get(detailEndpoint, {
                    withCredentials: true,
                    params: { view: "public" },
                });

                setHotel(res.data);
                const canonicalSlug = res.data?.redirect_slug || res.data?.slug;
                if (canonicalSlug && canonicalSlug !== id) {
                    navigate(`/companies/${canonicalSlug}${searchString ? `?${searchString}` : ""}`, { replace: true });
                }

                const companyId = res.data?.id;
                if (!captureMode && companyId && trackedViewRef.current !== String(companyId)) {
                    trackedViewRef.current = String(companyId);
                    axios.post(`${API_BASE}/api/companies/${companyId}/views`, {}, {
                        withCredentials: true
                    }).catch(() => {
                        // Tracking must never block the detail page.
                    });
                }

            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }

        };

        loadHotel();

    }, [id, captureMode, navigate, searchString]);

    useEffect(() => {
        if (!printMode || loading || !hotel) return undefined;

        const timer = window.setTimeout(() => {
            window.print();
        }, 1200);

        return () => window.clearTimeout(timer);
    }, [printMode, loading, hotel]);

    React.useLayoutEffect(() => {
        if (!hotel) return undefined;

        const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute("content") || "";
        const previousKeywords = document.querySelector('meta[name="keywords"]')?.getAttribute("content") || "";
        const previousTitle = document.title;

        setNamedMeta("description", hotel.seo_meta_description || buildFallbackMetaDescription(hotel));
        setNamedMeta("keywords", hotel.seo_meta_keywords || buildFallbackMetaKeywords(hotel));
        document.title = hotel.name ? `${hotel.name} - HRI` : previousTitle;

        return () => {
            setNamedMeta("description", previousDescription);
            setNamedMeta("keywords", previousKeywords);
            document.title = previousTitle;
        };
    }, [hotel]);

    const markers = useMemo(() => {

        if (!hotel) return [];

        const lat = Number(hotel.latitude);
        const lng = Number(hotel.longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];

        return [{
            id: hotel.id,
            slug: hotel.slug,
            name: hotel.name,
            street: hotel.street,
            zip: hotel.zip,
            city: hotel.city,
            country: hotel.country,
            position: [lat, lng],
            startImage: hotel.start_image_url,
            startImageCrop: hotel.start_image_crop,
        }];

    }, [hotel]);

    const openImage = (index) => {
        setActiveImage(index);
        setLightboxOpen(true);
    };

    const nextImage = () => {
        setActiveImage((prev) => (prev + 1) % hotel.images.length);
    };

    const prevImage = () => {
        setActiveImage((prev) =>
            prev === 0 ? hotel.images.length - 1 : prev - 1
        );
    };

    const openMenuPage = (index) => {
        setActiveMenuPage(index);
        setMenuLightboxOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!hotel) {
        return (
            <Container sx={{ py: 6 }}>
                <Typography variant="h5">
                    {t("hotel.notFound")}
                </Typography>
            </Container>
        );
    }

    const images = Array.isArray(hotel.images) ? hotel.images : [];
    const menus = Array.isArray(hotel.menus) ? hotel.menus : [];
    const logos = (Array.isArray(hotel.logos) ? hotel.logos : []).filter((logo) => logo.url);
    const menuPages = menus.flatMap((menu) => (
        (Array.isArray(menu.pages) ? menu.pages : []).map((page, pageIndex) => ({
            ...page,
            menuType: menu.type,
            menuLabel: menu.label,
            pageLabel: `Seite ${pageIndex + 1}`,
        }))
    ));
    const heroImg = resolveImg(hotel.start_image_url || images[0]?.url || images[0]);
    const roomQuantities = visibleRoomQuantities(hotel);
    const roomDetails = visibleRoomDetails(hotel);
    const roomPrices = visibleRoomPrices(hotel);
    const roomPriceSpecials = visibleRoomPriceSpecials(hotel);
    const roomPriceSettings = hotel.room_price_settings || {};
    const roomCurrency = roomPriceSettings.currency || "";
    const roomBreakfast = breakfastLabel(roomPriceSettings.breakfast_option);
    const hasRoomInfo = roomQuantities.length > 0 || roomDetails.length > 0;
    const hasRoomPrices = roomPrices.length > 0 ||
        roomPriceSettings.room_prices_url ||
        roomBreakfast ||
        roomPriceSpecials.length > 0 ||
        roomPriceSettings.room_info;

    const address = [
        hotel.street,
        [hotel.zip, hotel.city].filter(Boolean).join(" "),
        hotel.canton_state,
        hotel.country
    ]
        .filter(Boolean)
        .join(", ");

    const normalizeUrl = (url) => {
        if (!url) return "";
        if (!/^https?:\/\//i.test(url)) {
            return "https://" + url;
        }
        return url;
    };

    const chipSx = {
        bgcolor: "background.paper",
        borderColor: "rgba(240, 172, 77, 0.45)",
    };

    const renderTagGroup = (title, content) => (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: "text.primary" }}>
                {title}
            </Typography>
            {content}
        </Box>
    );

    const getMenuPageIndex = (menuType, pageIndex) => {
        const index = menuPages.findIndex((page) => page.menuType === menuType && Number(page.page_index) === pageIndex);
        if (index >= 0) return index;

        let offset = 0;
        for (const menu of menus) {
            const pages = Array.isArray(menu.pages) ? menu.pages : [];
            if (menu.type === menuType) return offset + pageIndex;
            offset += pages.length;
        }
        return 0;
    };

    const nextMenuPage = () => {
        setActiveMenuPage((prev) => (prev + 1) % menuPages.length);
    };

    const prevMenuPage = () => {
        setActiveMenuPage((prev) =>
            prev === 0 ? menuPages.length - 1 : prev - 1
        );
    };

    const activeMenu = menuPages[activeMenuPage] || menuPages[0];
    const activeMenuUrl = resolveImg(activeMenu?.url);

    return (
        <Box sx={{ width: "100%" }}>
            {captureMode && (
                <style>
                    {`
                        body { background: #fff !important; }
                        @media print {
                            @page { size: A4; margin: 12mm; }
                            body { background: #fff !important; }
                        }
                        .MuiAppBar-root,
                        footer,
                        .MuiButton-root,
                        .leaflet-control-container {
                            display: none !important;
                        }
                    `}
                </style>
            )}

            {heroImg && (
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: { xs: 260, sm: 340, md: 460 },
                        overflow: "hidden",
                        borderRadius: 2,
                        boxShadow: "0 20px 45px rgba(15, 23, 42, 0.16)",
                        bgcolor: "grey.200",
                    }}
                >
                    <img
                        src={heroImg}
                        alt={hotel.name}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        style={{ width: "100%", height: "100%", ...cropImageStyle(hotel.start_image_crop) }}
                    />
                    {hotel.company_logo_url && (
                        <Box component="img" src={resolveImg(hotel.company_logo_url)} alt={`Logo ${hotel.name}`}
                            sx={{ position: "absolute", left: { xs: 20, md: 32 }, bottom: { xs: 20, md: 28 },
                                width: { xs: 140, sm: 190 }, height: { xs: 110, sm: 150 }, objectFit: "contain",
                                bgcolor: "white", p: 1.5, boxSizing: "border-box", borderRadius: 1.5,
                                boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }} />
                    )}
                </Box>
            )}

            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.05fr) minmax(360px, 0.95fr)" },
                        gap: 3,
                        alignItems: "stretch",
                        mb: 3,
                    }}
                >
                    <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2, boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)" }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                                {hotel.company_logo_url && (
                                    <Box component="img" src={resolveImg(hotel.company_logo_url)} alt={`Logo ${hotel.name}`}
                                        sx={{ display: "block", width: { xs: 140, sm: 170 }, maxWidth: "100%", height: 110, objectFit: "contain", objectPosition: "left center", mb: 2 }} />
                                )}
                                <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.05, mb: 1 }}>
                                    {hotel.name}
                                </Typography>
                                {address && (
                                    <Typography color="text.secondary" sx={{ fontSize: 17 }}>
                                        {address}
                                    </Typography>
                                )}
                            </Box>

                            {(user?.is_admin || user?.role === "admin") && (
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={() =>
                                        window.open(
                                            `/admin/companies?edit=${hotel.id}`,
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                    sx={{ flexShrink: 0 }}
                                >
                                    Bearbeiten
                                </Button>
                            )}
                        </Box>

                        {(hotel.phone1 || hotel.phone2 || hotel.mobile_sms || hotel.telefax || hotel.email || hotel.reservations_email || hotel.homepage) && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>
                                    {t("hotel.contact")}
                                </Typography>
                                <Stack spacing={0.75}>
                                    {hotel.phone1 && <Typography>{t("hotel.phone")}: {hotel.phone1}</Typography>}
                                    {hotel.phone2 && <Typography>{t("hotel.phone2")}: {hotel.phone2}</Typography>}
                                    {hotel.mobile_sms && <Typography>Mobil: {hotel.mobile_sms}</Typography>}
                                    {hotel.telefax && <Typography>Telefax: {hotel.telefax}</Typography>}
                                    {hotel.email && (
                                        <Typography>
                                            {t("hotel.email")}: <Link href={`mailto:${hotel.email}`}>{hotel.email}</Link>
                                        </Typography>
                                    )}
                                    {hotel.reservations_email && (
                                        <Typography>
                                            Reservation: <Link href={`mailto:${hotel.reservations_email}`}>{hotel.reservations_email}</Link>
                                        </Typography>
                                    )}
                                    {hotel.homepage && (
                                        <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <LanguageIcon fontSize="small" />
                                            <Link href={normalizeUrl(hotel.homepage)} target="_blank" rel="noopener noreferrer">
                                                {t("hotel.website")}
                                            </Link>
                                        </Typography>
                                    )}
                                </Stack>
                            </>
                        )}

                        {(hotel.facebook || hotel.instagram || hotel.twitter) && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                <Stack direction="row" spacing={1}>
                                    {hotel.facebook && (
                                        <IconButton component="a" href={normalizeUrl(hotel.facebook)} target="_blank" rel="noopener noreferrer" aria-label="Facebook" color="primary">
                                            <FacebookIcon />
                                        </IconButton>
                                    )}
                                    {hotel.instagram && (
                                        <IconButton component="a" href={normalizeUrl(hotel.instagram)} target="_blank" rel="noopener noreferrer" aria-label="Instagram" color="primary">
                                            <InstagramIcon />
                                        </IconButton>
                                    )}
                                    {hotel.twitter && (
                                        <IconButton component="a" href={normalizeUrl(hotel.twitter)} target="_blank" rel="noopener noreferrer" aria-label="Twitter" color="primary">
                                            <TwitterIcon />
                                        </IconButton>
                                    )}
                                </Stack>
                            </>
                        )}

                        {(hotel.closing_days || hotel.summer_winter_season || hotel.company_holidays || hotel.opening_hours || hotel.info) && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                {(hotel.opening_hours || hotel.closing_days || hotel.summer_winter_season) && (
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                            gap: 2.5,
                                            mb: (hotel.company_holidays || hotel.info) ? 2 : 0,
                                        }}
                                    >
                                        {hotel.opening_hours && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                                                    {t("hotel.openingHours")}
                                                </Typography>
                                                <Box dangerouslySetInnerHTML={{ __html: hotel.opening_hours }} />
                                            </Box>
                                        )}
                                        {(hotel.closing_days || hotel.summer_winter_season) && (
                                            <Stack spacing={1.25}>
                                                {hotel.closing_days && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                                                            Ruhetage
                                                        </Typography>
                                                        <Typography color="text.secondary">{hotel.closing_days}</Typography>
                                                    </Box>
                                                )}
                                                {hotel.summer_winter_season && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                                                            Sommer- & Wintersaison
                                                        </Typography>
                                                        <Typography color="text.secondary">{hotel.summer_winter_season}</Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>
                                )}
                                {hotel.company_holidays && (
                                    <Box sx={{ mb: hotel.info ? 2 : 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                                            Betriebsferien
                                        </Typography>
                                        <Typography color="text.secondary">{hotel.company_holidays}</Typography>
                                    </Box>
                                )}
                                {hotel.info && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {t("hotel.info")}
                                        </Typography>
                                        <Box dangerouslySetInnerHTML={{ __html: hotel.info }} />
                                    </Box>
                                )}
                            </>
                        )}

                        {logos.length > 0 && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                                    {logos.map((logo) => (
                                        <Box
                                            key={logo.id}
                                            component="img"
                                            src={resolveImg(logo.url)}
                                            alt={logo.label_de || logo.name}
                                            title={logo.label_de || logo.name}
                                            sx={{
                                                maxWidth: 92,
                                                maxHeight: 38,
                                                objectFit: "contain",
                                                opacity: 0.76,
                                                filter: "saturate(0.85)",
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Paper>

                    <Paper sx={{ minHeight: { xs: 320, md: "100%" }, overflow: "hidden", borderRadius: 2, boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)" }}>
                        <Box sx={{ height: "100%", minHeight: { xs: 320, md: 460 }, width: "100%" }}>
                            <HotelsMap markers={markers} />
                        </Box>
                    </Paper>
                </Box>

                {images.length > 0 && (
                    <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 2, boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)" }}>
                        <Typography variant="h6" sx={{ px: 1, pt: 0.5, pb: 1.5, fontWeight: 800 }}>
                            Bilder
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                                gap: 1.5,
                            }}
                        >
                            {images.map((img, i) => {
                                const url = resolveImg(img.url || img);
                                return (
                                    <Box
                                        key={i}
                                        component="button"
                                        type="button"
                                        onClick={() => openImage(i)}
                                        sx={{
                                            width: "100%",
                                            aspectRatio: "4 / 3",
                                            overflow: "hidden",
                                            borderRadius: 1.5,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            cursor: "pointer",
                                            bgcolor: "grey.200",
                                            display: "block",
                                            p: 0,
                                            appearance: "none",
                                            transition: "transform 0.18s ease, box-shadow 0.18s ease",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
                                            },
                                            "&:hover img": {
                                                transform: "scale(1.035)",
                                            },
                                        }}
                                    >
                                        <img
                                            src={url}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                                transition: "transform 0.22s ease",
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                )}

                {menus.length > 0 && (
                    <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 2, boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)" }}>
                        <Typography variant="h6" sx={{ px: 0.5, pb: 1.5, fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
                            <RestaurantMenuIcon color="primary" />
                            Speisekarten
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: menus.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
                                    md: menus.length === 1 ? "1fr" : menus.length === 2 ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1.5,
                            }}
                        >
                            {menus.map((menu) => {
                                const pages = Array.isArray(menu.pages) ? menu.pages : [];
                                const externalUrl = menu.external_url ? normalizeUrl(menu.external_url) : "";
                                if (!pages.length && !externalUrl) return null;

                                const pageColumns = pages.length === 1
                                    ? "1fr"
                                    : pages.length === 2
                                        ? "repeat(2, minmax(0, 1fr))"
                                        : pages.length === 3
                                            ? "repeat(3, minmax(0, 1fr))"
                                            : "repeat(4, minmax(0, 1fr))";

                                return (
                                    <Paper key={menu.type} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, minWidth: 0 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                {menu.label}
                                            </Typography>
                                            <Chip size="small" variant="outlined" label={pages.length > 1 ? `${pages.length} Seiten` : "1 Seite"} />
                                        </Box>

                                        {externalUrl ? (
                                            <Button
                                                variant="outlined"
                                                href={externalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<OpenInNewIcon />}
                                                sx={{ justifyContent: "flex-start" }}
                                            >
                                                Link in neuem Tab öffnen
                                            </Button>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "1fr",
                                                        sm: pageColumns,
                                                    },
                                                    gap: 1,
                                                }}
                                            >
                                                {pages.map((page, pageIndex) => {
                                                const pageUrl = resolveImg(page.url);
                                                return (
                                                    <Box
                                                        key={page.id || pageIndex}
                                                        component="button"
                                                        type="button"
                                                        onClick={() => openMenuPage(getMenuPageIndex(menu.type, pageIndex))}
                                                        sx={{
                                                            width: "100%",
                                                            height: {
                                                                xs: 260,
                                                                sm: pages.length === 1 ? 520 : pages.length <= 2 ? 360 : 300,
                                                                md: menus.length === 1 && pages.length === 1 ? 640 : pages.length === 1 ? 520 : pages.length <= 2 ? 420 : 340,
                                                            },
                                                            overflow: "hidden",
                                                            borderRadius: 1,
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            bgcolor: "grey.100",
                                                            display: "block",
                                                            position: "relative",
                                                            p: 0,
                                                            cursor: "pointer",
                                                            textAlign: "left",
                                                            appearance: "none",
                                                            transition: "transform 0.18s ease, box-shadow 0.18s ease",
                                                            "&:hover": {
                                                                transform: "translateY(-2px)",
                                                                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={pageUrl}
                                                            alt={`${menu.label} Seite ${pageIndex + 1}`}
                                                            loading="lazy"
                                                            decoding="async"
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                display: "block",
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                right: 8,
                                                                bottom: 8,
                                                                bgcolor: "rgba(255,255,255,0.92)",
                                                                borderRadius: 1,
                                                                px: 0.75,
                                                                py: 0.35,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                color: "text.primary",
                                                            }}
                                                        >
                                                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                                                            <Typography variant="caption">Seite {pageIndex + 1}</Typography>
                                                        </Box>
                                                    </Box>
                                                );
                                                })}
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Paper>
                )}

                <Paper sx={{ p: { xs: 2.5, md: 3 }, mb: 3, borderRadius: 2, boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)" }}>
                    <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 800 }}>
                        Eigenschaften
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                            gap: 3,
                        }}
                    >
                        {hotel.categories && hotel.categories.length > 0 && renderTagGroup(
                            "Kategorien",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.categories.map((cat) => (
                                    <Chip key={cat.id} label={cat.label_de || cat.name} color="primary" />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.classifications) && hotel.classifications.length > 0 && renderTagGroup(
                            "Klassifizierungen",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.classifications.map((classification) => (
                                    <Chip
                                        key={classification.classification_id || classification.id}
                                        label={formatClassification(classification)}
                                        color={classification.is_primary ? "primary" : "default"}
                                        variant={classification.is_primary ? "filled" : "outlined"}
                                        icon={<ClassificationIcon iconName={classification.icon_name} />}
                                    />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.hotel_groups) && hotel.hotel_groups.length > 0 && renderTagGroup(
                            "Hotelgruppen",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.hotel_groups.map((group) => {
                                    const slug = (group.slug || "").trim();
                                    return (
                                        <Chip
                                            key={group.id}
                                            label={group.label_de || group.name}
                                            variant="outlined"
                                            component={slug ? "a" : "div"}
                                            href={slug ? `https://www.hri.ch/hotelgruppe/${slug}` : undefined}
                                            target={slug ? "_blank" : undefined}
                                            rel={slug ? "noopener noreferrer" : undefined}
                                            clickable={!!slug}
                                        />
                                    );
                                })}
                            </Stack>
                        )}

                        {Array.isArray(hotel.awards) && hotel.awards.length > 0 && renderTagGroup(
                            "Auszeichnungen",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.awards.map((award) => (
                                    <AwardBadge
                                        key={award.id}
                                        award={award}
                                        size={24}
                                        sx={{ fontSize: 14, fontWeight: 500 }}
                                    />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.cuisines) && hotel.cuisines.length > 0 && renderTagGroup(
                            "Küche",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.cuisines.map((cuisine) => (
                                    <Chip key={cuisine.id} label={cuisine.label_de || cuisine.name} variant="outlined" sx={chipSx} />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.amenities) && hotel.amenities.length > 0 && renderTagGroup(
                            "Ausstattung",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.amenities.map((amenity) => (
                                    <Chip key={amenity.id} icon={amenity.icon_name ? <MaterialIcon iconName={amenity.icon_name} /> : undefined} label={amenity.label_de || amenity.name} variant="outlined" sx={chipSx} />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.restaurant_prices) && hotel.restaurant_prices.length > 0 && renderTagGroup(
                            "Preise",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.restaurant_prices.map((price) => (
                                    <Chip key={price.id} label={price.label_de || price.name} variant="outlined" sx={chipSx} />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.delivery_services) && hotel.delivery_services.length > 0 && renderTagGroup(
                            "Lieferservice",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.delivery_services.map((service) => (
                                    <Chip key={service.id} label={service.label_de || service.name} variant="outlined" sx={chipSx} />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.locations) && hotel.locations.length > 0 && renderTagGroup(
                            "Standorte / Umgebung",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.locations.map((location) => (
                                    <Chip
                                        key={location.id}
                                        label={location.label_de || location.name}
                                        icon={<LocationIcon iconName={location.icon_name} />}
                                        variant="outlined"
                                        sx={chipSx}
                                    />
                                ))}
                            </Stack>
                        )}

                        {Array.isArray(hotel.leisure_activities) && hotel.leisure_activities.length > 0 && renderTagGroup(
                            "Freizeit & Sport",
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {hotel.leisure_activities.map((activity) => (
                                    <Chip
                                        key={activity.id}
                                        label={activity.label_de || activity.name}
                                        icon={<LeisureActivityIcon iconName={activity.icon_name} />}
                                        variant="outlined"
                                        sx={chipSx}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Paper>

                {(hasRoomInfo || hasRoomPrices) && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: hasRoomInfo && hasRoomPrices ? "repeat(2, minmax(0, 1fr))" : "1fr",
                            },
                            alignItems: "stretch",
                            gap: 3,
                            mb: 4,
                        }}
                    >
                        {hasRoomInfo && (
                            <Paper sx={{ p: 3, height: "100%" }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Zimmer
                                </Typography>

                                {roomQuantities.length > 0 && (
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
                                            gap: 1.5,
                                            mb: roomDetails.length > 0 ? 2.5 : 0,
                                        }}
                                    >
                                        {roomQuantities.map((item) => (
                                            <Box key={item.id} sx={{ minWidth: 0 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                    {item.quantity}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {(item.label_de || item.name || "").replace(/^Anzahl\s+/i, "")}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {roomDetails.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                            Zimmer mit
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {roomDetails.map((item) => (
                                                <Chip
                                                    key={item.id}
                                                    label={item.custom_label || item.label_de || item.name}
                                                    variant="outlined"
                                                    sx={{
                                                        bgcolor: "background.paper",
                                                        borderColor: "rgba(240, 172, 77, 0.45)",
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Paper>
                        )}

                        {hasRoomPrices && (
                            <Paper sx={{ p: 3, height: "100%" }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Zimmerpreise
                                </Typography>

                                {roomPriceSettings.room_prices_url && (
                                    <Typography sx={{ mb: 2 }}>
                                        <Link href={normalizeUrl(roomPriceSettings.room_prices_url)} target="_blank" rel="noopener noreferrer">
                                            Detaillierte Zimmerpreise
                                        </Link>
                                    </Typography>
                                )}

                                {roomPrices.length > 0 && (
                                    <Box sx={{ display: "grid", gap: 1.25, mb: 2.5 }}>
                                        {roomPrices.map((item) => {
                                            const label = item.custom_label || item.label_de || item.name;
                                            const price = formatRoomPrice(item, roomCurrency);
                                            return (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {label}{item.reference ? ` (${item.reference} Personen)` : ""}
                                                    </Typography>
                                                    {price && (
                                                        <Typography color="text.secondary">
                                                            {price}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                {(roomBreakfast || roomPriceSpecials.length > 0) && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: roomPriceSettings.room_info ? 2 : 0 }}>
                                        {roomBreakfast && <Chip label={roomBreakfast} variant="outlined" />}
                                        {roomPriceSpecials.map((item) => (
                                            <Chip
                                                key={item.id}
                                                label={item.custom_label || item.label_de || item.name}
                                                variant="outlined"
                                                sx={{
                                                    bgcolor: "background.paper",
                                                    borderColor: "rgba(240, 172, 77, 0.45)",
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {roomPriceSettings.room_info && (
                                    <Typography color="text.secondary">
                                        {roomPriceSettings.room_info}
                                    </Typography>
                                )}
                            </Paper>
                        )}
                    </Box>
                )}

            </Box>

            {hotel.images && hotel.images.length > 0 && (

                <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth={false} fullWidth>

                    <Box sx={{ position: "relative", width: "100%", height: "90vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#000" }}>

                        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: "absolute", top: 20, right: 20, color: "#fff" }}>
                            <CloseIcon />
                        </IconButton>

                        {hotel.images.length > 1 && (
                            <>
                                <IconButton onClick={prevImage} sx={{ position: "absolute", left: 20, color: "#fff" }}>
                                    <ArrowBackIosNewIcon />
                                </IconButton>

                                <IconButton onClick={nextImage} sx={{ position: "absolute", right: 20, color: "#fff" }}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </>
                        )}

                        <img
                            src={resolveImg(hotel.images[activeImage]?.url || hotel.images[activeImage])}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />

                    </Box>

                </Dialog>

            )}

            {menuPages.length > 0 && (

                <Dialog open={menuLightboxOpen} onClose={() => setMenuLightboxOpen(false)} maxWidth={false} fullWidth>

                    <Box sx={{ position: "relative", width: "100%", height: "92vh", display: "grid", gridTemplateRows: "auto minmax(0, 1fr) auto", background: "#101214" }}>

                        <Box
                            sx={{
                                px: { xs: 2, md: 3 },
                                py: 1.5,
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                borderBottom: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                                    {activeMenu?.menuLabel || "Speisekarte"}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                                    {activeMenuPage + 1} / {menuPages.length}
                                </Typography>
                            </Box>

                            <IconButton onClick={() => setMenuLightboxOpen(false)} sx={{ color: "#fff" }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ position: "relative", minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 1.5, md: 3 } }}>
                            {menuPages.length > 1 && (
                                <IconButton
                                    onClick={prevMenuPage}
                                    sx={{
                                        position: "absolute",
                                        left: { xs: 8, md: 20 },
                                        color: "#fff",
                                        bgcolor: "rgba(0,0,0,0.35)",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                                        zIndex: 1,
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </IconButton>
                            )}

                            {activeMenuUrl && (
                                <Box
                                    component="img"
                                    src={activeMenuUrl}
                                    alt={`${activeMenu?.menuLabel || "Speisekarte"} ${activeMenu?.pageLabel || ""}`}
                                    sx={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                        bgcolor: "background.paper",
                                        boxShadow: "0 24px 70px rgba(0,0,0,0.45)",
                                    }}
                                />
                            )}

                            {menuPages.length > 1 && (
                                <IconButton
                                    onClick={nextMenuPage}
                                    sx={{
                                        position: "absolute",
                                        right: { xs: 8, md: 20 },
                                        color: "#fff",
                                        bgcolor: "rgba(0,0,0,0.35)",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                                        zIndex: 1,
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            )}
                        </Box>

                        {menuPages.length > 1 && (
                            <Box
                                sx={{
                                    px: { xs: 1.5, md: 3 },
                                    py: 1.5,
                                    display: "flex",
                                    gap: 1,
                                    overflowX: "auto",
                                    borderTop: "1px solid rgba(255,255,255,0.12)",
                                }}
                            >
                                {menuPages.map((page, index) => {
                                    const thumbUrl = resolveImg(page.url);
                                    const selected = index === activeMenuPage;
                                    return (
                                        <Box
                                            key={page.id || `${page.menuType}-${index}`}
                                            component="button"
                                            type="button"
                                            onClick={() => setActiveMenuPage(index)}
                                            sx={{
                                                flex: "0 0 76px",
                                                height: 96,
                                                p: 0,
                                                border: "2px solid",
                                                borderColor: selected ? "primary.main" : "rgba(255,255,255,0.22)",
                                                borderRadius: 1,
                                                overflow: "hidden",
                                                bgcolor: "background.paper",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={thumbUrl}
                                                alt={`${page.menuLabel} ${page.pageLabel}`}
                                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                    </Box>

                </Dialog>

            )}

        </Box>
    );
}
